<template>
  <v-app-bar
    :class="menuClass"
    dense
    dark
    elevate-on-scroll
  >
    <div class="ukpl-main-navigation-container">
      <div class="navigation-left-wrap">
        <a href="https://www.ukpl.co.uk">
          <v-img
            src="@/assets/ukpl-logo-new.png"
            alt="UKPL logo"
            contain
            class="ukpl-nav-logo"
          />
        </a>
      </div>
      <v-spacer />
      <v-toolbar-items
        v-if="currentUser"
        class="hidden-sm-and-down"
      >
        <v-btn
          v-if="isAdmin"
          color="#0072ff"
          depressed
          :to="'/players'"
          router
        >
          Players
        </v-btn>
        <v-btn
          v-if="isAdmin"
          color="#0072ff"
          depressed
          :to="'/teams'"
          router
        >
          Teams
        </v-btn>
        <v-btn
          v-if="isAdmin"
          color="#0072ff"
          depressed
          :to="'/signin'"
          router
        >
          Sign In
        </v-btn>
        <!-- CPPS CMS dropdown -->
        <v-menu
          v-if="isAdmin"
          offset-y
        >
          <template #activator="{ on, attrs }">
            <v-btn
              color="#0072ff"
              depressed
              v-bind="attrs"
              v-on="on"
            >
              CPPS <v-icon
                right
                small
              >
                mdi-chevron-down
              </v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="'/cpps/events'">
              <v-list-item-title>Events Manager</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/news'">
              <v-list-item-title>News</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/faq'">
              <v-list-item-title>FAQ</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/prices'">
              <v-list-item-title>Prices</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/rules'">
              <v-list-item-title>Rules</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/markers'">
              <v-list-item-title>Markers / Shop</v-list-item-title>
            </v-list-item>
            <v-divider />
            <v-list-item :to="'/cpps/scores'">
              <v-list-item-title>Score Entry</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/training'">
              <v-list-item-title>Training Manager</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/roundsetup'">
              <v-list-item-title>Round Setup</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/new-team'">
              <v-list-item-title>New Team</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/roundactions'">
              <v-list-item-title>Round Actions</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/schedule'">
              <v-list-item-title>Generate Schedule</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/custom-schedule'">
              <v-list-item-title>Custom Event Schedule</v-list-item-title>
            </v-list-item>
            <v-list-item :to="'/cpps/podium-print'">
              <v-list-item-title>Podium Printout</v-list-item-title>
            </v-list-item>
            <v-divider />
            <v-list-item @click="openClearDialog">
              <v-list-item-title class="red--text">
                Clear Bookings
              </v-list-item-title>
            </v-list-item>
            <v-divider />
            <v-list-item :to="'/cpps/logs'">
              <v-list-item-title>Log Viewer</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn
          color="#0072ff"
          depressed
          @click.native="logOut"
        >
          <v-icon class="mr-2">
            fa-sign-out-alt
          </v-icon>LogOut
        </v-btn>
      </v-toolbar-items>
      <v-toolbar-items
        v-else
        class="hidden-sm-and-down"
      >
        <v-btn
          color="#0072ff"
          depressed
          to="/home"
          router
        >
          Login
        </v-btn>
      </v-toolbar-items>
      <v-toolbar-items class="hidden-md-and-up">
        <v-app-bar-nav-icon @click="$emit('toggleDrawer')" />
      </v-toolbar-items>
    </div>

    <!-- Confirmation Dialog for Clear Bookings -->
    <v-dialog
      v-model="clearDialog"
      max-width="450"
    >
      <v-card>
        <v-card-title class="text-h6 red--text">
          Clear Bookings
        </v-card-title>
        <v-card-text>
          Are you sure you want to clear all bookings? This will truncate the <code>cppsbooking</code> table and cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="clearing"
            @click="clearDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="clearing"
            @click="executeClearBookings"
          >
            Clear Bookings
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="4000"
      top
    >
      {{ snackbarText }}
    </v-snackbar>
  </v-app-bar>
</template>

<script>
import BookingService from '@/services/booking.service';

export default {
  name: 'UkplMenu',
  props: {
    currentUser: { type: Object, default: null },
    isAdmin: { type: Boolean, default: false },
    menuClass: { type: String, default: 'ukpl-main-navigation' },
    logOut: { type: Function, default: null }
  },
  data: () => ({
    clearDialog: false,
    clearing: false,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success'
  }),
  methods: {
    openClearDialog() {
      this.clearDialog = true;
    },
    async executeClearBookings() {
      this.clearing = true;
      try {
        await BookingService.clearBookings();
        this.clearDialog = false;
        this.snackbarText = 'Bookings table (cppsbooking) truncated successfully.';
        this.snackbarColor = 'success';
        this.snackbar = true;
      } catch (err) {
        console.error('Failed to clear bookings:', err);
        this.snackbarText = 'Failed to clear bookings.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.clearing = false;
      }
    }
  }
};
</script>

